export const SendEmailDocumentationMarkdown = `
# Send Email Block

The Send Email Block sends an e-mail from the specified sender to the recipient's email address.

## Configuration

- **Sender Email**: The sender's email address. Must be from an authorized domain configured for your organization. Typically noreply@<previsit-domain>.
- **Sender Name**: The sender's display name.
- **Recipient Email**: The email of the person receiving the e-mail.
- **Subject**: The subject of the e-mail to be sent.
- **Content**: The content of the e-mail to be sent.
- **Timeout**: The maximum duration to wait for the e-mail to be sent before failing.

## Email Content

The **Content** field allows you to compose the body of your email. You can create either a rich HTML email or a plain text email.

- To edit the content, click the **Open HTML** button.
- From the HTML view, you can switch to a plain text editor by clicking **Edit Plain Text**.
- Click **View HTML** to preview the results.

## Sender Email Requirements

The sender email must be from an authorized domain that has been configured for your organization. Contact **Notable Support** or your **Customer Success representative** to add new email domains to your organization.

## Using Flow Data in Email Content

You can insert data from other blocks in the flow into the **Subject** and **Content** fields. This allows you to personalize the email.

The syntax for referencing data from another block is: \`{{BlockName.data.path.to.value}}\`

- \`BlockName\` is the name of the block as it appears on the flow canvas.
- \`data\` is a keyword.
- \`path.to.value\` is the path to the data within the block's output.

For example, to include the patient's first name from the Start block, you would use: \`{{Start.data.entity.firstName}}\`.

You can view the available data from a block by inspecting its output after a successful run of the flow.

## Using Email Dynamic Parameters

You can insert some of the e-mail dynamic parameters into the **Subject** and **Content** fields.

The syntax for referencing these parameters is: \`{{parameter}}\`

Those parameters are:
- **currentEpoch**: This is the date and time relative to which a computer's clock and timestamp values are determined.
- **logoImgUrl**: This is the web address for the image set up in the Admin application under **Organization >> Branding**.
- **notableLogoUrl**: This is the web address for the powered by Notable image.
- **themeButtonBackground**: This is the color set up in the Admin application under **Organization >> Branding**, e.g. Blue.
- **unsubscribeUrl**: This is the web address for the unsubscribe link.

All of these parameters can be previewed by using the **View HTML** button in the **Content** field editor.

## Examples of Common Use Cases

### Appointment Reminders
Send personalized appointment reminders with patient details:
- **Subject**: \`{{Start.data.entity.firstName}}, your appointment reminder\`
- **Content**: Include appointment date, time, and location from flow data

### Welcome Emails
Send welcome emails to new patients:
- **Subject**: \`Welcome to our practice, {{Start.data.entity.firstName}}!\`
- **Content**: Include practice information, logo, and next steps

### Follow-up Communications
Send follow-up emails after procedures or consultations:
- **Subject**: \`Follow-up: {{Start.data.entity.procedureName}}\`
- **Content**: Include recovery instructions, contact information, and next appointment details

### Survey Requests
Send patient satisfaction surveys:
- **Subject**: \`We value your feedback, {{Start.data.entity.firstName}}\`
- **Content**: Include survey link and thank you message

## Other Useful Tips

### HTML Email Best Practices
- Use tables for layout to ensure compatibility across email clients
- Keep images under 1MB and host them on a reliable server
- Test your emails across different email clients (Gmail, Outlook, Apple Mail)

### Personalization Strategies
- Include dynamic content like appointment times, provider names, or procedure details
- Personalize subject lines to improve open rates

### Email Deliverability
- Keep sender email consistent across all communications
- Use a clear, recognizable sender name
- Avoid spam trigger words in subject lines
- Include an unsubscribe link in all marketing emails

### Testing Your Emails
- Use the **Open HTML** feature to preview how your email will look
- Verify all block template values resolve correctly
- Check that links and images load properly (you may need to send the email to yourself to test this, links may not load properly in the preview tool)

## Error Conditions

### Common Error Scenarios

**Invalid Sender Email**
- **Cause**: Sender email is not using the correct authorized domain for your organization.
- **Solution**: Use an email address from your organization's approved domain list. Contact **Notable Support** or your **Customer Success representative** to add new email domains to your organization.

**Invalid Recipient Email**
- **Cause**: Recipient email format is invalid or empty
- **Solution**: Verify the email address format and ensure it's not empty

**Content Errors**
- **Cause**: Invalid HTML syntax or missing required fields
- **Solution**: Validate HTML content and ensure all required fields are populated

**Block Template Value Errors**
- **Cause**: Referenced block data doesn't exist or is malformed
- **Solution**: Verify block names and data paths are correct

**Rate Limiting**:
- **Cause**: Too many email requests in a short period may trigger rate limiting from the service provider.
- **Example**: If you are testing sending emails to yourself, you may run into the limit of only being able to receive 24 emails per day.
- **Solution**: Wait for the rate limit to expire before trying again.
`;
